/**
 * Chat History Component
 * Displays conversation history with date grouping
 *
 * Accessibility:
 * - Semantic list structure
 * - 44px minimum touch targets
 * - Loading states with skeletons
 * - Proper heading hierarchy
 * - Respects reduced motion preferences
 */

import React from 'react';
import { motion } from 'motion/react';
import { X, MessageSquare, Loader2 } from 'lucide-react';
import { format, isToday, isYesterday, formatDistanceToNow } from 'date-fns';
import type { ChatConversation } from '../types/chat.types';
import { cn } from '../../../../lib/utils';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';
import { ConversationHistorySkeleton } from './ChatSkeletons';

interface ChatHistoryProps {
  conversations: ChatConversation[];
  onSelectConversation: (conversationId: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
  onClose: () => void;
  hasMore?: boolean;
  onLoadMore?: () => Promise<void>;
  isLoadingMore?: boolean;
  isInitialLoading?: boolean;
  /** ID of conversation currently being loaded (shows spinner on that item) */
  loadingConversationId?: string | null;
  className?: string;
}

/**
 * Format conversation date for display
 */
function formatConversationDate(timestamp: number): string {
  const date = new Date(timestamp);

  if (isToday(date)) {
    return 'Today';
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  const daysDiff = Math.floor((Date.now() - timestamp) / (1000 * 60 * 60 * 24));
  if (daysDiff <= 7) {
    return format(date, 'EEEE');
  }

  return format(date, 'MMM d, yyyy');
}

/**
 * Group conversations by date
 */
function groupConversationsByDate(conversations: ChatConversation[]): Record<string, ChatConversation[]> {
  const groups: Record<string, ChatConversation[]> = {};

  conversations.forEach(conv => {
    const dateKey = formatConversationDate(conv.lastActivityAt);
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(conv);
  });

  return groups;
}

/**
 * Chat history panel component
 * Shows previous conversations grouped by date
 */
export const ChatHistory: React.FC<ChatHistoryProps> = ({
  conversations,
  onSelectConversation,
  onDeleteConversation: _onDeleteConversation,
  onClose,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  isInitialLoading = false,
  loadingConversationId = null,
  className
}) => {
  const { prefersReducedMotion } = useReducedMotion();
  const groupedConversations = groupConversationsByDate(conversations);

  return (
    <motion.aside
      initial={prefersReducedMotion ? { opacity: 0 } : { x: '100%' }}
      animate={prefersReducedMotion ? { opacity: 1 } : { x: 0 }}
      exit={prefersReducedMotion ? { opacity: 0 } : { x: '100%' }}
      transition={prefersReducedMotion ? { duration: 0 } : {
        type: 'spring',
        damping: 30,
        stiffness: 300,
      }}
      className={cn(
        'absolute inset-0 z-20',
        'bg-white dark:bg-gray-900',
        'flex flex-col',
        'rounded-l-2xl',
        'shadow-lg border-l border-gray-200 dark:border-gray-700',
        'max-md:rounded-none max-md:border-0',
        className
      )}
      role="complementary"
      aria-label="Chat history"
    >
      {/* Header */}
      <header className="h-16 px-4 bg-linear-to-r from-emerald-600 to-emerald-700 flex items-center justify-between rounded-tl-2xl max-md:rounded-none">
        <h2 className="text-lg font-display font-semibold text-white">
          Chat History
        </h2>
        <button
          onClick={onClose}
          className={cn(
            'w-10 h-10 min-w-[44px] min-h-[44px]',
            'rounded-full bg-white/15 hover:bg-white/25',
            'text-white',
            'flex items-center justify-center',
            'transition-colors duration-150',
            'focus:outline-hidden focus:ring-2 focus:ring-white/50'
          )}
          aria-label="Close history"
          type="button"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </header>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto p-4 bg-linear-to-b from-gray-50/50 to-white dark:from-gray-800/50 dark:to-gray-900">
        {/* Initial Loading State */}
        {isInitialLoading ? (
          <ConversationHistorySkeleton count={4} />
        ) : conversations.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16" role="status">
            <MessageSquare className="w-14 h-14 text-gray-300 dark:text-gray-600 mx-auto mb-4" aria-hidden="true" />
            <p className="text-gray-600 dark:text-gray-400 font-medium mb-1">
              No conversations yet
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Your conversations will appear here
            </p>
          </div>
        ) : (
          /* Conversation Groups */
          <nav aria-label="Conversation list">
            {Object.entries(groupedConversations).map(([dateGroup, convs]) => (
              <div key={dateGroup} className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">
                  {dateGroup}
                </h3>
                <ul className="space-y-2" role="list">
                  {convs
                    .sort((a, b) => b.lastActivityAt - a.lastActivityAt)
                    .map((conv) => {
                      const lastMessage = conv.messages[conv.messages.length - 1];
                      const timeAgo = formatDistanceToNow(new Date(conv.lastActivityAt), {
                        addSuffix: true
                      });
                      const isLoading = loadingConversationId === conv.id;

                      return (
                        <li key={conv.id}>
                          <motion.button
                            onClick={() => !isLoading && onSelectConversation(conv.id)}
                            disabled={isLoading}
                            className={cn(
                              'w-full p-4 rounded-xl',
                              'min-h-[64px]', // Touch target
                              'bg-white dark:bg-gray-800',
                              !isLoading && 'hover:bg-gray-50 dark:hover:bg-gray-750',
                              'border border-gray-200/60 dark:border-gray-700/60',
                              'text-left transition-all duration-150',
                              'shadow-xs',
                              !isLoading && 'hover:shadow-md',
                              'focus:outline-hidden focus:ring-2 focus:ring-emerald-500/50',
                              isLoading && 'opacity-80 cursor-wait ring-2 ring-emerald-500/30'
                            )}
                            whileHover={prefersReducedMotion || isLoading ? {} : { y: -1 }}
                            whileTap={prefersReducedMotion || isLoading ? {} : { scale: 0.99 }}
                            type="button"
                            aria-label={isLoading
                              ? `Loading conversation: ${conv.title || 'Untitled'}`
                              : `Open conversation: ${conv.title || 'Untitled'}, ${timeAgo}`}
                            aria-busy={isLoading}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-base font-medium text-gray-900 dark:text-white truncate mb-1">
                                  {conv.title || 'New Conversation'}
                                </p>
                                {lastMessage && (
                                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate leading-relaxed mb-1">
                                    {lastMessage.content.substring(0, 60)}
                                    {lastMessage.content.length > 60 ? '...' : ''}
                                  </p>
                                )}
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                  {timeAgo}
                                </p>
                              </div>
                              {isLoading && (
                                <Loader2
                                  className="w-5 h-5 text-emerald-600 animate-spin shrink-0 mt-0.5"
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                          </motion.button>
                        </li>
                      );
                    })}
                </ul>
              </div>
            ))}
          </nav>
        )}

        {/* Load More Button */}
        {hasMore && onLoadMore && (
          <div className="mt-6 flex justify-center">
            <motion.button
              onClick={onLoadMore}
              disabled={isLoadingMore}
              className={cn(
                'px-6 py-3 rounded-xl',
                'min-h-[48px]', // Touch target
                'bg-emerald-600 hover:bg-emerald-700 text-white',
                'font-medium text-sm',
                'shadow-xs hover:shadow-md',
                'transition-colors duration-150',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'focus:outline-hidden focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2',
                'flex items-center gap-2'
              )}
              whileHover={prefersReducedMotion || isLoadingMore ? {} : { scale: 1.01 }}
              whileTap={prefersReducedMotion || isLoadingMore ? {} : { scale: 0.99 }}
              type="button"
              aria-busy={isLoadingMore}
            >
              {isLoadingMore && (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              )}
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </motion.button>
          </div>
        )}
      </div>
    </motion.aside>
  );
};

export default ChatHistory;
